<template>
  <div>
    <main-large-dialog
      :is-visible.sync="computedIsVisible"
      :title="$i18n.t('dialog.selectObjectsUsingSqlQuery')"
      agree-text="dialog.agreeSelect"
      :is-valid="isQueryValid"
      :loading="isSelecting"
      dialog-width="60%"
      max-height="90vh"
      no-paddings-x
      header-shadow
      @agree="runSqlQueryHandler"
    >
      <template #body>
        <v-col cols="12">
          <main-input-dialog-row
            name="sqlQueryId"
            v-model="sqlQueryId"
            :data-type="{ name: 'select' }"
            translation-path="dialog.body.favoriteQueries"
            class="pb-6 px-5 border-bottom"
            leftCol="3"
            align="center"
            :items="filteredSqlQueries"
            itemText="name"
            itemValue="id"
            hide-details
            clearable
            @click:clear="onClear"
          />
          <v-row class="pt-6 px-5" no-gutters>
            <v-col cols="6" align-self="end">
              <v-row align="center" no-gutters>
                <v-col cols="auto" class="text-h6">
                  {{ $i18n.t('dialog.body.sqlQuery') }}
                </v-col>
                <v-col cols="auto" class="ml-6">
                  <v-tooltip color="font" top>
                    <template #activator="{ on }">
                      <v-btn
                        v-on="on"
                        :color="!isSqlQuerySelected ? '#e0e0e0' : '#1a73e81a'"
                        :disabled="!isSqlQuerySelected && !payload.query"
                        depressed
                        fab
                        small
                        @click="!!isSqlQuerySelected ? openDeleteSqlQueryDialog() : openAddSqlQueryDialog()"
                      >
                        <v-icon :color="isSqlQuerySelected ? 'primary' : 'font'">
                          {{ !!isSqlQuerySelected ? 'mdi-heart' : 'mdi-heart-outline' }}
                        </v-icon>
                      </v-btn>
                    </template>
                    {{ !!isSqlQuerySelected ? $i18n.t('dialog.deleteFromFavorite') : $i18n.t('dialog.addToFavorite') }}
                  </v-tooltip>
                  <v-tooltip color="font" top>
                    <template #activator="{ on }">
                      <v-btn
                        v-on="on"
                        class="ml-2"
                        color="primary"
                        :disabled="!isSqlQueryChanged"
                        :loading="isLoading"
                        depressed
                        fab
                        small
                        @click="editSqlQueryHandler"
                      >
                        <v-icon>
                          mdi-content-save
                        </v-icon>
                        <template #loader>
                          <main-loader color="dark" />
                        </template>
                      </v-btn>
                    </template>
                    {{ $i18n.t('dialog.saveChanges') }}
                  </v-tooltip>
                </v-col>
              </v-row>
              <data-input
                v-if="!!isSqlQuerySelected"
                v-model="payload['name']"
                :data-type="{ name: 'text' }"
                :label="$i18n.t('dialog.queryName')"
                class="pt-6"
                hide-details
              />
              <v-select
                v-model="payload['layers']"
                :label="$i18n.t('dialog.body.layersInQuery')"
                :items="filteredProjectLayers"
                item-text="name"
                item-value="id"
                class="pt-6 input-layers"
                dense
                chips
                multiple
                outlined
                :hide-details="areAllLayersAdded"
                :error="isSqlQuerySelected && !areAllLayersAdded"
                :error-messages="isSqlQuerySelected && !areAllLayersAdded ? $i18n.t('dialog.body.toRunSqlQuery') : []"
              >
                <template #selection="{ item, index }">
                  <v-chip v-if="item.id === mainLayerId" disabled>
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <v-chip
                    v-if="index < 4 && item.id !== mainLayerId"
                    close
                    @click:close="payload['layers'].splice(index, 1)"
                  >
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <span v-if="index === 4" class="text-caption grey--text">
                    {{ `(+${$i18n.tc('admin.other', payload['layers'].length - 4)})` }}
                  </span>
                </template>
                <template #item="{active, item, attrs, on}">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }" :disabled="item.id === mainLayerId">
                    <v-list-item-action>
                      <v-checkbox :input-value="active" />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row class="pt-6" no-gutters>
            <v-col cols="8" class="px-5">
              <data-input
                ref="sqlQuery"
                v-model="payload['query']"
                :data-type="{ name: 'textarea' }"
                class="input-query"
                clearable
                hide-details
                rows="12"
                no-resize
                @click:clear="onQueryInputClear"
              >
                <template #prepend-inner>
                  <span class="text-left text--disabled">{{ fromClause }}</span>
                </template>
              </data-input>
            </v-col>
            <v-col cols="4" class="d-flex flex-column pr-5">
              <v-chip-group class="mb-2" column>
                <v-chip
                  v-for="(operator, idx) in operators"
                  :key="idx"
                  style="border: 1px solid #d5ebfe"
                  color="rgba(228, 242, 254, 0.6)"
                  :ripple="false"
                  label
                  @click="addToQuery(operator, true)"
                >
                  {{ operator }}
                </v-chip>
                <div style="flex-grow: 1" />
              </v-chip-group>
              <v-treeview :items="selectedLayers" class="flex-grow-1 text-left treeview" hoverable dense>
                <template #label="{ item }">
                  <v-hover #default="{ hover }">
                    <div class="d-flex justify-space-between align-center">
                      <span class="treeview__label">
                        {{ item.name }}
                      </span>
                      <v-tooltip v-if="hover" color="font" bottom>
                        <template #activator="{ on }">
                          <v-btn
                            v-on="on"
                            color="success"
                            height="18"
                            width="18"
                            depressed
                            fab
                            @click="addToQuery({ dataSourceName: item.dataSourceName, attr: item.name })"
                          >
                            <v-icon small>
                              mdi-plus
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $i18n.t('dialog.body.addToQuery') }}</span>
                      </v-tooltip>
                    </div>
                  </v-hover>
                </template>
              </v-treeview>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </main-large-dialog>
    <main-input-dialog
      v-if="isAddSqlQueryOpened"
      :is-visible.sync="isAddSqlQueryVisible"
      :title-translation="$i18n.t('dialog.title.addToFavorite')"
      agree-text="dialog.agreeAdd"
      :inputs="inputSqlQueryName"
      :loading="isLoading"
      @agree="addSqlQueryHandler"
    >
      <template #sqlQueryName.input="data">
        <v-col cols="6">
          <data-input v-model="payload['name']" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isDeleteSqlQueryOpened"
      :is-visible.sync="isDeleteSqlQueryVisible"
      :title="$i18n.t('dialog.title.deleteFromFavorite')"
      :body="$i18n.t('dialog.body.deleteFavoriteQueryConfirmation')"
      agree-text="dialog.agreeDelete"
      :loading="isLoading"
      @agree="deleteSqlQueryHandler"
    />
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { isEqual } from 'lodash';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import MainLoader from '@/components/MainLoader.vue';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainInputDialogRow from '@/components/MainInputDialogRow';
import DataInput from '@/components/DataInput';

export default {
  name: 'SqlQueryDialog',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    MainLoader,
    MainLargeDialog,
    MainInputDialogRow,
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    mainLayerId: {
      type: Number,
      required: true,
    },
    projectLayers: {
      type: Array,
      required: true,
    },
    layers: {
      type: Object,
      required: true,
    },
    layersMetadata: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    payload: {
      main_layer: null,
      name: '',
      layers: [],
      query: '',
    },
    sqlQueryId: null,
    isLoading: false,
    isSelecting: false,
    isAddSqlQueryOpened: false,
    isAddSqlQueryVisible: false,
    isDeleteSqlQueryOpened: false,
    isDeleteSqlQueryVisible: false,
    operators: ['=', '!=', '>', '>=', '<', '<=', 'IN', 'LIKE', 'IS NULL', 'BETWEEN', 'AND', 'OR', 'NOT'],
  }),
  computed: {
    selectedFeatures: get('map/selectedFeatures'),
    sqlQueries: get('sqlQuery/sqlQueries'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.sqlQueryId = null;
        this.payload.name = '';
        this.payload.layers = [this.mainLayerId];
        this.payload.query = '';
        this.$emit('update:isVisible', nV);
      },
    },
    filteredSqlQueries() {
      return this.sqlQueries.filter(query => query.main_layer === this.mainLayerId) || [];
    },
    sqlLayers() {
      return this.projectLayers?.some(layer => layer.id === this.mainLayerId)
        ? this.projectLayers
        : [this.layers[this.mainLayerId], ...this.projectLayers];
    },
    filteredProjectLayers() {
      return this.sqlLayers?.filter(layer => layer.geometry_type) || [];
    },
    filteredProjectLayersIds() {
      return this.filteredProjectLayers.map(layer => layer.id);
    },
    isSqlQuerySelected() {
      return !!this.sqlQueryId || this.sqlQueryId === 0;
    },
    selectedSqlQuery() {
      return this.sqlQueries.find(query => query.id === this.sqlQueryId) || {};
    },
    selectedLayers() {
      return (
        this.filteredProjectLayers
          ?.filter(layer => this.payload.layers?.includes(layer.id))
          .map(el => {
            return {
              id: el.id,
              name: el.name,
              children: this.getAttributes(el.id, el.data_source_name, el.name),
            };
          }) || []
      );
    },
    isSqlQueryChanged() {
      if (this.isSqlQuerySelected) {
        const { name, layers, query } = this.selectedSqlQuery;
        const newLayers = this.payload.layers.filter(layerId => layerId !== this.mainLayerId);
        return name !== this.payload.name || !isEqual(layers || [], newLayers) || query !== this.payload.query;
      } else {
        return false;
      }
    },
    areAllLayersAdded() {
      const layers = this.selectedSqlQuery.layers || [];
      const selectedLayers = layers ? [this.mainLayerId, ...layers] : [this.mainLayerId];
      return this.isSqlQuerySelected
        ? selectedLayers.every(layerId => this.filteredProjectLayersIds.includes(layerId))
        : true;
    },
    addedSqlQuery() {
      return this.sqlQueries.find(query => query.name === this.payload.name)?.id || null;
    },
    isQueryValid() {
      return !!this.payload.layers && !!this.payload.query && this.areAllLayersAdded;
    },
    fromClause() {
      const { name } = this.layers[this.mainLayerId];
      return `SELECT "${name}"."ID" FROM "${name}"`;
    },
    inputSqlQueryName() {
      return [
        {
          name: 'sqlQueryName',
          translationPath: 'dialog.queryName',
          dataType: { name: 'text' },
          cols: 12,
          leftCol: 5,
          rightCol: 6,
          spacer: true,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
  },
  watch: {
    mainLayerId: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.payload.main_layer = nV;
          this.payload.layers = [nV];
        }
      },
    },
    sqlQueryId(nV) {
      if (nV) {
        const { name, main_layer, layers, query } = JSON.parse(JSON.stringify(this.filteredSqlQueries)).find(
          query => query.id === nV
        );
        const sqlLayers = layers ? layers.filter(layerId => this.filteredProjectLayersIds.includes(layerId)) : [];
        this.payload.name = name;
        this.payload.layers = sqlLayers ? [main_layer, ...sqlLayers] : [main_layer];
        this.payload.query = query;
      }
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...call('sqlQuery/*SqlQuery'),
    async runSqlQueryHandler() {
      try {
        this.isSelecting = true;
        const layers = this.payload.layers.filter(layerId => layerId !== this.mainLayerId);
        const data = { ...this.payload, layers };
        delete data.name;
        const selectedFeatures = await this.runSqlQuery(data);
        this.deleteFeatures(this.mainLayerId, this.selectedFeatures[this.mainLayerId]);
        this.selectFeatures(this.mainLayerId, selectedFeatures);
        this.computedIsVisible = false;
      } finally {
        this.isSelecting = false;
      }
    },
    async addSqlQueryHandler() {
      try {
        this.isLoading = true;
        const layers = this.payload.layers.filter(layerId => layerId !== this.mainLayerId);
        await this.addSqlQuery({ ...this.payload, layers });
        this.sqlQueryId = this.addedSqlQuery;
      } finally {
        this.isLoading = false;
        this.isAddSqlQueryOpened = false;
        this.isAddSqlQueryVisible = false;
      }
    },
    async editSqlQueryHandler() {
      try {
        this.isLoading = true;
        const { name } = this.selectedSqlQuery;
        const layers = this.payload.layers.filter(layerId => layerId !== this.mainLayerId);
        const data = {
          id: this.sqlQueryId,
          payload: { ...this.payload, layers },
        };
        name === this.payload.name && delete data.payload.name;
        await this.editSqlQuery(data);
      } finally {
        this.isLoading = false;
      }
    },
    async deleteSqlQueryHandler() {
      try {
        this.isLoading = true;
        await this.deleteSqlQuery(this.sqlQueryId);
        this.onClear();
      } finally {
        this.isLoading = false;
        this.isDeleteSqlQueryOpened = false;
        this.isDeleteSqlQueryVisible = false;
      }
    },
    selectFeatures(layerId, results) {
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: results,
        },
      });
    },
    deleteFeatures(layerId, ids) {
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: ids ? [...ids] : [],
        },
      });
    },
    onClear() {
      this.sqlQueryId = null;
      this.payload.layers = [this.mainLayerId];
      this.payload.name = '';
      this.payload.query = '';
    },
    onQueryInputClear() {
      setTimeout(() => {
        this.payload.query = '';
      });
    },
    openAddSqlQueryDialog() {
      this.isAddSqlQueryOpened = true;
      this.isAddSqlQueryVisible = true;
    },
    openDeleteSqlQueryDialog() {
      this.isDeleteSqlQueryOpened = true;
      this.isDeleteSqlQueryVisible = true;
    },
    async init() {
      await this.getSqlQuery();
    },
    getAttributes(layerId, dataSource, dataSourceName) {
      const formShema = this.getForm(
        this.layers[layerId]?.form_schema.elements || [],
        this.layersMetadata[dataSource].attributes_schema.attributes
      );
      const attributes =
        this.$_flattenStructure(formShema, 'elements')
          ?.filter(el => el.attribute !== this.layersMetadata[dataSource].attributes_schema.id_name)
          ?.map(el => {
            return { name: el.label, dataSourceName };
          }) || [];
      return [{ name: 'id', dataSourceName }, { name: 'geom', dataSourceName }, ...attributes];
    },
    addToQuery(value, isOperator = false) {
      const cursorPosition = this.$refs.sqlQuery.$refs['data-input'].$refs.input.$refs.input.selectionStart;
      let text;
      if (isOperator) {
        text = ` ${value} `;
      } else {
        const { dataSourceName, attr } = value;
        text = dataSourceName ? `"${dataSourceName}"."${attr}"` : `"${attr}"`;
      }
      this.payload.query =
        this.payload.query.slice(0, cursorPosition) + text + this.payload.query.slice(cursorPosition);
      this.$refs.sqlQuery.$refs['data-input'].$refs.input.$refs.input.focus();
      this.$nextTick(() => {
        const textEnd = this.payload.query.slice(0, cursorPosition).length + text.length;
        this.$refs.sqlQuery.$refs['data-input'].$refs.input.$refs.input.setSelectionRange(textEnd, textEnd);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select .v-chip {
    margin: 2px;
  }
  .v-select.input-layers .v-icon.mdi-menu-down {
    padding-top: 8px !important;
  }
  .input-layers .v-select__selections {
    padding-top: 8px !important;
    flex-wrap: wrap;
  }
  .v-list-item__title {
    text-align: left;
  }
  .v-list-item--disabled {
    display: none;
  }
  .v-textarea textarea {
    margin-top: 0 !important;
  }
  .v-textarea.v-text-field--enclosed.v-text-field--outlined.v-input--dense textarea {
    padding-right: 0 !important;
  }
  .input-query {
    span {
      position: absolute;
      left: 16px;
    }
    .v-text-field__slot {
      padding-top: 30px !important;
      padding-bottom: 12px;
    }
    .v-text-field--outlined fieldset {
      top: 0;
    }
  }
  .treeview {
    height: 0;
    background-color: rgba(228, 242, 254, 0.6);
    border: 1px solid #d5ebfe;
    border-radius: 4px;
    overflow-y: scroll;
    &__label {
      width: calc(100% - 20px);
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
  .v-slide-group__content {
    padding: 0;
    justify-content: space-between;
  }
  .v-treeview--dense .v-treeview-node__root {
    min-height: 30px;
  }
}
.border-bottom {
  @include border(bottom, 1px);
}
</style>
